import { useCallback, useState } from 'react';
import { extractKustoError } from '../../api/KustoAccessApi';

/**
 * Encapsulates the busy/error lifecycle shared by the Kusto access actions (lookup, grant) so each
 * form doesn't re-implement the same try/catch + setState boilerplate. Result/message state stays
 * with the caller; this hook owns only the "in flight" and "last error" state.
 *
 * `run` and `clearError` keep a stable identity, so a caller that needs a stable callback should
 * depend on those rather than on the returned object.
 *
 * Usage:
 * ```ts
 * const lookup = useKustoRequest();
 * const run = () => lookup.run(async () => { ...await api...; setResult(...); });
 * // lookup.busy / lookup.error drive the UI
 * ```
 */
export function useKustoRequest(): {
    busy: boolean;
    error: string | null;
    run: (operation: () => Promise<void>) => Promise<void>;
    clearError: () => void;
} {
    const [busy, setBusy] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const run = useCallback(async (operation: () => Promise<void>) => {
        setError(null);
        setBusy(true);
        try {
            await operation();
        } catch (err) {
            setError(extractKustoError(err));
        } finally {
            setBusy(false);
        }
    }, []);

    const clearError = useCallback(() => setError(null), []);

    return { busy, error, run, clearError };
}
